import { toJS, isObservable } from 'mobx'

/**
 * Identity-stable plain-object snapshots of MobX observables for passing
 * across the Module Federation boundary.
 *
 * Why not bare toJS() at the call site:
 * - toJS() returns a NEW object on every call. Host pages that render MFE
 *   loaders re-render freely (@observer), so bare toJS() would hand the MFE
 *   a new props identity per render, re-triggering its hydration useEffects
 *   (e.g. checkout-mfe re-firing accountsContainer.getProfile()).
 * - Passing the raw observable is not safe either: host and remote can end
 *   up on different MobX instances (module-federation shared drift), and
 *   cross-instance observable assignment fails silently.
 *
 * The WeakMap caches one snapshot per observable reference: the snapshot is
 * recomputed only when the underlying reference changes (i.e. the store
 * actually re-assigned it after a refetch) — exactly the identity semantics
 * the MFE effects were written against.
 */
const snapshotCache = new WeakMap()

export function toPlainSnapshot(observableValue) {
  if (observableValue === null || observableValue === undefined) {
    return observableValue
  }
  if (typeof observableValue !== 'object') {
    return observableValue
  }
  if (!isObservable(observableValue)) {
    // Already plain (e.g. hydrated from SSR JSON) — pass through untouched.
    return observableValue
  }
  if (snapshotCache.has(observableValue)) {
    return snapshotCache.get(observableValue)
  }
  const snapshot = toJS(observableValue)
  snapshotCache.set(observableValue, snapshot)
  return snapshot
}
